import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import PersonaCheckbox from './PersonaCheckbox';
import React from 'react';
import SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import strings from 'owa-strings/owa-mini-strings';
import { FolderLabel } from './FolderLabel';
import { getAriaSummaryLabel } from '../utils/getAriaSummaryLabel';
import { getFilteredMessageClassIcon } from '../utils/getFilteredMessageClassIcon';
import { getPropertyIcons } from '../utils/getPropertyIcons';
import { handleKeyPress } from '../utils/handleKeys';
import { highlightTermsInHtmlElement } from 'dom-utils/lib/utils/highlightUtils';
import { isCurrentMajorVersionViable } from 'mini-common-utils/lib/utils/isCurrentMajorVersionViable';
import { MailListItemIconBar } from './MailListItemIconBar';
import { MailListItemTimeStamp } from './MailListItemTimeStamp';
import { PropertyIcons } from '../schema/PropertyIcons';
import { RichContents } from '../schema/RichContents';
import {
    DARK_MODE_ACCESSIBILITY_BUG_FIX_EDGE_VERSION,
    DARK_MODE_CONSTANTS,
} from 'mini-theme/lib/utils/dynamicModeChange';

const styles = require('./MailListItem.scss');
const classNames = require('classnames/bind').bind(styles);

const DATA_TEST_ID = 'MailListItem';
export const MAIL_LIST_ITEM_CUSTOM_ATTRIBUTE_NAME = 'data-focus-id';
export const MAIL_LIST_ITEMS_SELECTOR = `[${MAIL_LIST_ITEM_CUSTOM_ATTRIBUTE_NAME}]`;

/**
 * These styles are static, hence moving them out
 */
const bundleNameStylesFirstLine = classNames(styles['mailListItem-firstLine'], 'ellipisOnOverflow');

const bundleNameStylesSecondLine = classNames(
    styles['mailListItem-secondLine'],
    'ellipisOnOverflow'
);

const bundleNameStylesPreview = classNames(styles['mailListItem-preview'], 'ellipisOnOverflow');

type SupportedItemCardClassNames =
    | 'mailListItemCard'
    | 'mailListItemCard-withoutRsvp'
    | 'mailListItemCard-withoutPreview'
    | 'mailListItemCard-withoutPreview-withoutRsvp';

export interface MailListItemProps {
    effectiveMentioned: boolean;
    firstLineText: string;
    hasAttachment: boolean;
    importance: ImportanceType;
    isFlagged: boolean;
    isMeetingMessage: boolean;
    isPinned: boolean;
    isSelected: boolean;
    messageClassIcon: PropertyIcons;
    lastDeliveryTimestamp: string;
    likeCount: number;
    rowId: string;
    secondLineText: string;
    showDraftsIndicator: boolean;
    thirdLineText: string;
    unreadCount: number;
    lastSender: SingleRecipientType;
    isInCheckedMode: boolean;
    shouldShowAvatar: boolean;
    shouldShowFolderLabel?: boolean;
    parentFolderName?: string;
    termsToHighlight?: string[];
    onMultiSelect?: (rowId: string) => void;
    richContents?: RichContents;
}

export const MailListItem = React.forwardRef(
    (props: MailListItemProps, ref?: React.RefObject<HTMLElement>) => {
        const {
            rowId,
            unreadCount,
            isFlagged,
            isPinned,
            isSelected,
            messageClassIcon,
            firstLineText,
            secondLineText,
            thirdLineText,
            lastSender,
            showDraftsIndicator,
            lastDeliveryTimestamp,
            isInCheckedMode,
            shouldShowAvatar,
            shouldShowFolderLabel = false,
            parentFolderName,
            termsToHighlight,
            onMultiSelect,
            richContents,
        } = props;

        // needed for mark or un-mark at every render call
        const highlightTermsWrapper = (element: HTMLElement) => {
            if (element && termsToHighlight?.length > 0) {
                highlightTermsInHtmlElement(element, termsToHighlight);
            }
        };

        const isUnread: boolean = unreadCount > 0;

        const showRSVPUnit = !!richContents?.RSVPUnit?.Component;
        const filteredMessageClassIcon = getFilteredMessageClassIcon(
            showRSVPUnit,
            messageClassIcon
        );

        const outerContainerClassNames = classNames(
            {
                'mailListItem--unread': isUnread,
                'mailListItem--selected': isSelected,
                'mailListItem--noAvatar': !shouldShowAvatar,
            },
            styles['mailListItem--showLeftContainer'],
            styles.mailListItemAdditionalStyle,
            styles.mailListItem,
            calculateBackgroundColorClass(isFlagged, isPinned, isSelected),
            getMailListItemCardClassName(!!thirdLineText, showRSVPUnit)
        );
        const pressedStateBehavior = {
            onTouchStart: () => {
                ref?.current?.classList?.add(styles.mailListItemPressed);
            },
            onTouchEnd: () => {
                ref?.current?.classList?.remove(styles.mailListItemPressed);
            },
        };

        function onKeyDownHandler(e: React.KeyboardEvent) {
            handleKeyPress(e, rowId, onMultiSelect);
        }

        const propertyIcons: PropertyIcons[] = getPropertyIcons(props);

        const containerAriaLabel = getAriaSummaryLabel(props);

        let initialBackgroundColor = 'none';

        function emailHighLightProvider() {
            if (
                isCurrentMajorVersionViable(DARK_MODE_ACCESSIBILITY_BUG_FIX_EDGE_VERSION) &&
                window.matchMedia('(prefers-color-scheme: dark)').matches
            ) {
                initialBackgroundColor = document.getElementById('mailItemContainer' + rowId).style
                    .backgroundColor;
                if (isSelected)
                    document.getElementById('mailItemContainer' + rowId).style.backgroundColor =
                        DARK_MODE_CONSTANTS.selectedBG;
                else if (isFlagged)
                    document.getElementById('mailItemContainer' + rowId).style.backgroundColor =
                        DARK_MODE_CONSTANTS.flaggedBG;
                else if (isPinned)
                    document.getElementById('mailItemContainer' + rowId).style.backgroundColor =
                        DARK_MODE_CONSTANTS.pinnedBG;
            }
        }

        function emailHighLightRemover() {
            if (
                isCurrentMajorVersionViable(DARK_MODE_ACCESSIBILITY_BUG_FIX_EDGE_VERSION) &&
                window.matchMedia('(prefers-color-scheme: dark)').matches
            ) {
                document.getElementById(
                    'mailItemContainer' + rowId
                ).style.backgroundColor = initialBackgroundColor;
            }
        }

        return (
            <div id={'mailItemContainer' + rowId}>
                <article
                    ref={ref}
                    tabIndex={-1}
                    aria-label={containerAriaLabel}
                    onKeyDown={onKeyDownHandler}
                    onFocus={ev => {
                        ev.currentTarget.setAttribute('aria-selected', 'true');
                        emailHighLightProvider();
                    }}
                    onBlur={(ev: React.FocusEvent<HTMLElement>) => {
                        if (!ev.currentTarget) return;
                        emailHighLightRemover();
                        if (ev.relatedTarget instanceof Node) {
                            // If focus goes to some other element except one of the children node then mark the
                            // list-item as not selected. This usually happens when user clicks on some header/footer elements
                            // or presses Tab or Shift+Tab
                            if (!ev.currentTarget?.contains(ev.relatedTarget)) {
                                ev.currentTarget.setAttribute('aria-selected', 'false');
                            }
                        }
                    }}
                    className={outerContainerClassNames}
                    {...pressedStateBehavior}
                    {...{ [MAIL_LIST_ITEM_CUSTOM_ATTRIBUTE_NAME]: rowId }}
                    data-testid={DATA_TEST_ID}>
                    <PersonaCheckbox
                        rowId={rowId}
                        isSelected={isSelected}
                        isInCheckedMode={isInCheckedMode}
                        lastSender={lastSender}
                        onMultiSelect={onMultiSelect}
                        isAvatarEnabled={shouldShowAvatar}
                    />
                    <div className={styles['mailListItem-container']}>
                        <div role="button" aria-label={containerAriaLabel}>
                            <div className={styles['mailListItem-content']}>
                                <div
                                    className={bundleNameStylesFirstLine}
                                    ref={highlightTermsWrapper}>
                                    {showDraftsIndicator && (
                                        <span className={styles.draftPlaceHolderText}>
                                            {strings.draftSenderPersonaPlaceholder}
                                        </span>
                                    )}
                                    {firstLineText}
                                </div>
                                <MailListItemIconBar
                                    className={styles['mailListItem--iconBar']}
                                    isFlagged={isFlagged}
                                    isPinned={isPinned}
                                    unreadCount={unreadCount}
                                    propertyIcons={propertyIcons}
                                    messageClassIcon={filteredMessageClassIcon}
                                    rowId={rowId}
                                />
                            </div>
                            <div className={styles['mailListItem-content']}>
                                <h2
                                    className={bundleNameStylesSecondLine}
                                    ref={highlightTermsWrapper}>
                                    {secondLineText}
                                </h2>
                                <MailListItemTimeStamp
                                    className={styles['mailListItem-timestamp']}
                                    timeString={lastDeliveryTimestamp}
                                />
                            </div>
                            {thirdLineText && (
                                <div className={styles['mailListItem-content']}>
                                    <div
                                        className={bundleNameStylesPreview}
                                        ref={highlightTermsWrapper}>
                                        {thirdLineText}
                                    </div>
                                    {unreadCount > 1 && (
                                        <div className={styles['mailListItem-unreadCount']}>
                                            {unreadCount}
                                        </div>
                                    )}
                                    {shouldShowFolderLabel && (
                                        <FolderLabel
                                            folderName={parentFolderName}
                                            isUnread={isUnread}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        {richContents?.RSVPUnit?.Component}
                    </div>
                </article>
            </div>
        );
    }
);

const calculateBackgroundColorClass = (
    isFlagged: boolean,
    isPinned: boolean,
    isSelected: boolean
): string => {
    if (isSelected) {
        return styles.selectedMailListItemColor;
    } else if (isFlagged) {
        return styles.flaggedMailListItemColor;
    } else if (isPinned) {
        return styles.pinnedMailListItemColor;
    }

    return styles.regularMailListItemColor;
};

const getMailListItemCardClassName = (
    showPreviewText: boolean,
    showRSVPUnit: boolean
): SupportedItemCardClassNames => {
    if (showPreviewText && showRSVPUnit) {
        return 'mailListItemCard';
    }
    if (showPreviewText) {
        return 'mailListItemCard-withoutRsvp';
    }
    if (showRSVPUnit) {
        return 'mailListItemCard-withoutPreview';
    }
    return 'mailListItemCard-withoutPreview-withoutRsvp';
};
